import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  Loader2,
  Users,
  Network,
  BookUser,
  BarChart3,
  Palette,
  Building2,
  Clock,
  Sparkles,
  ArrowRight,
  Gift,
  Home,
  ChevronDown
} from 'lucide-react';

// Icons mapping for options
const OPTION_ICONS = {
  dg: Building2,
  drh: Users,
  rh_manager: Users,
  manager: Users,
  consultant: Users,
  trombinoscope: BookUser,
  organigramme: Network,
  profiles: Users,
  teams: Building2,
  analytics: BarChart3
};

// All available plans for selection
const ALL_PLANS = {
  free: {
    id: 'free',
    name: 'STARTER',
    displayName: 'STARTER',
    description: 'Pour Startups & PME en croissance (20 à 100 employés)',
    pricePerUser: 0,
    features: {
      maxEmployees: 100,
      trombinoscope: true,
      organigramme: true,
      importExport: true,
      personnalisation: false,
      analytics: true,
      permissions: 'standard'
    }
  },
  pro: {
    id: 'pro',
    name: 'BUSINESS',
    displayName: 'BUSINESS',
    description: 'Pour entreprises, groupes et organisations (+100 employés)',
    pricePerUser: 7,
    features: {
      maxEmployees: -1,
      trombinoscope: true,
      organigramme: true,
      organigrammeAdvanced: true,
      importExport: true,
      personnalisation: true,
      analytics: true,
      teams: true,
      permissions: 'advanced'
    }
  },
  enterprise: {
    id: 'enterprise',
    name: 'ENTERPRISE',
    displayName: 'Enterprise',
    description: 'Grandes structures',
    pricePerUser: 12,
    features: {
      maxEmployees: -1,
      trombinoscope: true,
      organigramme: true,
      organigrammeAdvanced: true,
      importExport: true,
      personnalisation: true,
      brandingComplet: true,
      analytics: true,
      analyticsAdvanced: true,
      teams: true,
      multiEntreprise: true,
      permissions: 'advanced',
      sla: true,
      prioritySupport: true
    }
  }
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [analysisMessages, setAnalysisMessages] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [recommendation, setRecommendation] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null); // User can override recommendation
  const [showPackSelector, setShowPackSelector] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // UTM params
  const utmSource = searchParams.get('utm_source');
  const utmMedium = searchParams.get('utm_medium');
  const utmCampaign = searchParams.get('utm_campaign');

  useEffect(() => {
    fetchQuestionnaire();
  }, []);

  const fetchQuestionnaire = async () => {
    try {
      const response = await api.get('/onboarding/questions');
      setQuestions(response.data.steps || []);
      setAnalysisMessages(response.data.analysisMessages || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const nextStep = () => {
    if (step < questions.length) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const startAnalysis = async () => {
    setAnalyzing(true);
    setStep(questions.length + 1); // Analysis step

    // Animate through analysis messages
    for (let i = 0; i < analysisMessages.length; i++) {
      setAnalysisStep(i);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Get recommendation
    try {
      const response = await api.post('/onboarding/analyze', responses);
      setRecommendation(response.data.recommendation);
      setStep(questions.length + 2); // Recommendation step
    } catch (error) {
      toast.error('Erreur lors de l\'analyse');
      setStep(questions.length - 1);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async () => {
    if (!contactInfo.firstName || !contactInfo.lastName || !contactInfo.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      const leadData = {
        ...contactInfo,
        ...responses,
        utmSource,
        utmMedium,
        utmCampaign
      };

      const response = await api.post('/onboarding/submit', leadData);
      
      if (response.data.success) {
        toast.success('Inscription réussie !');
        // Redirect to signup or dashboard
        navigate('/login', { 
          state: { 
            email: contactInfo.email,
            recommendedPack: recommendation?.pack?.id
          }
        });
      }
    } catch (error) {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  const currentQuestion = questions[step];
  const progress = questions.length > 0 ? ((step) / (questions.length + 2)) * 100 : 0;
  const canProceed = step < questions.length ? responses[currentQuestion?.id] !== undefined : true;
  
  // Get the active pack (user selected or recommended)
  const activePack = selectedPack ? ALL_PLANS[selectedPack] : recommendation?.pack;
  const activePromotion = selectedPack === 'free' ? null : recommendation?.promotion;
  const activePricing = selectedPack 
    ? { 
        basePrice: ALL_PLANS[selectedPack].pricePerUser * (recommendation?.pricing?.estimatedEmployees || 50),
        discountedPrice: selectedPack === 'free' ? 0 : ALL_PLANS[selectedPack].pricePerUser * (recommendation?.pricing?.estimatedEmployees || 50) * (activePromotion ? (1 - activePromotion.discount / 100) : 1),
        pricePerUser: ALL_PLANS[selectedPack].pricePerUser,
        estimatedEmployees: recommendation?.pricing?.estimatedEmployees || 50
      }
    : recommendation?.pricing;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-10 h-10 bg-webtech-blue rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">W</span>
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">HR Engine</span>
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-webtech-blue transition-colors"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Retour à l'accueil</span>
            </button>
            <div className="text-sm text-slate-500">
              {step <= questions.length && `Étape ${step + 1} / ${questions.length + 2}`}
            </div>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto">
          <Progress value={progress} className="h-1 rounded-none" />
        </div>
      </div>

      {/* Content */}
      <main className="max-w-2xl mx-auto py-12 px-4">
        {/* Questions Steps */}
        {step < questions.length && currentQuestion && (
          <div className="animate-fade-in">
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-heading">
                  {currentQuestion.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid gap-3">
                  {currentQuestion.options.map((option) => {
                    const Icon = OPTION_ICONS[option.value] || Check;
                    const isSelected = responses[currentQuestion.id] === option.value;
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleOptionSelect(currentQuestion.id, option.value)}
                        className={`
                          flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all
                          ${isSelected 
                            ? 'border-webtech-blue bg-webtech-blue/5' 
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                          }
                        `}
                        data-testid={`option-${option.value}`}
                      >
                        <div className={`
                          w-12 h-12 rounded-xl flex items-center justify-center transition-colors
                          ${isSelected ? 'bg-webtech-blue text-white' : 'bg-slate-100 text-slate-600'}
                        `}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <span className={`font-medium ${isSelected ? 'text-webtech-blue' : 'text-slate-700'}`}>
                          {option.label}
                        </span>
                        {isSelected && (
                          <Check className="w-5 h-5 text-webtech-blue ml-auto" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-8">
                  <Button
                    variant="outline"
                    onClick={prevStep}
                    disabled={step === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Retour
                  </Button>
                  
                  {step === questions.length - 1 ? (
                    <Button
                      onClick={startAnalysis}
                      disabled={!canProceed}
                      className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                    >
                      Voir ma recommandation
                      <Sparkles className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={nextStep}
                      disabled={!canProceed}
                      className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                    >
                      Continuer
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analysis Step */}
        {step === questions.length + 1 && analyzing && (
          <div className="text-center py-16 animate-fade-in">
            <div className="w-20 h-20 bg-webtech-blue/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <Loader2 className="w-10 h-10 animate-spin text-webtech-blue" />
            </div>
            <div className="space-y-4">
              {analysisMessages.map((msg, index) => (
                <p 
                  key={index}
                  className={`text-lg transition-all duration-500 ${
                    index <= analysisStep ? 'text-slate-900 opacity-100' : 'text-slate-400 opacity-50'
                  }`}
                >
                  {index <= analysisStep && <Check className="w-5 h-5 inline mr-2 text-green-500" />}
                  {msg}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Recommendation Step */}
        {step === questions.length + 2 && recommendation && (
          <div className="space-y-6 animate-fade-in">
            {/* Pack Recommendation */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className={`${selectedPack ? 'bg-slate-700' : 'bg-webtech-blue'} text-white p-6 text-center`}>
                <p className="text-sm opacity-80 mb-2">
                  {selectedPack ? 'Plan sélectionné' : 'Votre plan recommandé'}
                </p>
                <h2 className="text-3xl font-heading font-bold">
                  {activePack?.displayName}
                </h2>
                <p className="mt-2 opacity-80">{activePack?.description}</p>
              </div>
              
              <CardContent className="p-6">
                {/* Pricing */}
                <div className="text-center mb-6">
                  {activePromotion && activePack?.pricePerUser > 0 && (
                    <Badge className="bg-webtech-yellow text-slate-900 mb-3">
                      <Gift className="w-3 h-3 mr-1" />
                      -{activePromotion.discount}% {activePromotion.validityHours ? `valable ${activePromotion.validityHours}h` : 'première année'}
                    </Badge>
                  )}
                  
                  <div className="flex items-center justify-center gap-3">
                    {activePromotion && activePricing?.basePrice > 0 && (
                      <span className="text-2xl text-slate-400 line-through">
                        ${activePricing.basePrice}
                      </span>
                    )}
                    <span className="text-5xl font-bold text-webtech-blue">
                      ${activePricing?.discountedPrice || 0}
                    </span>
                    <span className="text-slate-500">/mois</span>
                  </div>
                  
                  <p className="text-sm text-slate-500 mt-2">
                    {activePricing?.pricePerUser > 0 
                      ? `${activePricing.pricePerUser}$ par utilisateur × ~${activePricing.estimatedEmployees} utilisateurs`
                      : 'Gratuit jusqu\'à 10 utilisateurs'
                    }
                  </p>
                </div>

                {/* Promotion Banner */}
                {activePromotion && activePack?.pricePerUser > 0 && (
                  <div className="bg-gradient-to-r from-webtech-yellow/20 to-amber-100 rounded-xl p-4 mb-6 border border-webtech-yellow/30">
                    <p className="text-center font-medium text-slate-800">
                      {activePromotion.message}
                    </p>
                  </div>
                )}

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <h4 className="font-semibold text-slate-900">Ce plan inclut :</h4>
                  <div className="grid gap-2">
                    {activePack && Object.entries(activePack.features).map(([key, value]) => {
                      if (value === false) return null;
                      
                      const labels = {
                        maxEmployees: value === -1 ? 'Collaborateurs illimités' : `Jusqu'à ${value} collaborateurs`,
                        trombinoscope: 'Trombinoscope dynamique',
                        organigramme: 'Organigrammes interactifs',
                        organigrammeAdvanced: 'Organigrammes avancés multi-niveaux',
                        importExport: 'Import/Export CSV & Excel',
                        personnalisation: 'Personnalisation (logo + couleurs)',
                        brandingComplet: 'Branding complet',
                        analytics: 'Analytics structurels',
                        analyticsAdvanced: 'Analytics avancés',
                        teams: 'Gestion équipes / départements',
                        multiEntreprise: 'Multi-entreprises',
                        permissions: value === 'advanced' ? 'Permissions avancées' : 'Permissions standards',
                        sla: 'SLA garanti',
                        prioritySupport: 'Support prioritaire'
                      };
                      
                      const label = labels[key];
                      if (!label) return null;
                      
                      return (
                        <div key={key} className="flex items-center gap-2 text-slate-600">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Change Plan Button */}
                <div className="border-t border-slate-200 pt-4">
                  <button
                    onClick={() => setShowPackSelector(!showPackSelector)}
                    className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-webtech-blue transition-colors py-2"
                  >
                    <span>Choisir un autre plan</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showPackSelector ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Pack Selector */}
                  {showPackSelector && (
                    <div className="mt-4 grid gap-3">
                      {Object.values(ALL_PLANS).map((plan) => {
                        const isRecommended = plan.id === recommendation.pack.id;
                        const isSelected = selectedPack === plan.id || (!selectedPack && isRecommended);
                        
                        return (
                          <button
                            key={plan.id}
                            onClick={() => {
                              setSelectedPack(plan.id === recommendation.pack.id ? null : plan.id);
                              setShowPackSelector(false);
                            }}
                            className={`
                              flex items-center justify-between p-4 rounded-xl border-2 text-left transition-all
                              ${isSelected 
                                ? 'border-webtech-blue bg-webtech-blue/5' 
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                              }
                            `}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`
                                w-10 h-10 rounded-lg flex items-center justify-center
                                ${isSelected ? 'bg-webtech-blue text-white' : 'bg-slate-100 text-slate-600'}
                              `}>
                                {plan.id === 'free' && <Gift className="w-5 h-5" />}
                                {plan.id === 'pro' && <Sparkles className="w-5 h-5" />}
                                {plan.id === 'enterprise' && <Building2 className="w-5 h-5" />}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className={`font-semibold ${isSelected ? 'text-webtech-blue' : 'text-slate-900'}`}>
                                    {plan.displayName}
                                  </span>
                                  {isRecommended && (
                                    <Badge variant="outline" className="text-xs border-webtech-blue text-webtech-blue">
                                      Recommandé
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-500">{plan.description}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`text-lg font-bold ${isSelected ? 'text-webtech-blue' : 'text-slate-900'}`}>
                                ${plan.pricePerUser}
                              </span>
                              <span className="text-sm text-slate-500">/user/mois</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Contact Form */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle>Créez votre compte</CardTitle>
                <CardDescription>
                  Complétez vos informations pour commencer
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Prénom *</Label>
                    <Input
                      id="firstName"
                      value={contactInfo.firstName}
                      onChange={(e) => setContactInfo({ ...contactInfo, firstName: e.target.value })}
                      data-testid="contact-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input
                      id="lastName"
                      value={contactInfo.lastName}
                      onChange={(e) => setContactInfo({ ...contactInfo, lastName: e.target.value })}
                      data-testid="contact-lastname"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email professionnel *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                    data-testid="contact-email"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      value={contactInfo.phone}
                      onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Entreprise</Label>
                    <Input
                      id="companyName"
                      value={contactInfo.companyName}
                      onChange={(e) => setContactInfo({ ...contactInfo, companyName: e.target.value })}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full h-12 bg-webtech-blue hover:bg-webtech-blue-hover text-white text-lg mt-4"
                  data-testid="submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    <>
                      {(activePack?.pricePerUser || 0) > 0 ? "S'abonner maintenant" : "Créer mon compte gratuit"}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-slate-500 mt-4">
                  En créant un compte, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
